/**
 * pops.ts — synthesized sound effects for Poparium, built on top of
 * AudioEngine's shared context/master chain/voice cap. Every sound is
 * generated from oscillators and a single shared white-noise buffer; no
 * samples, no libraries.
 */

import {
  claimVoice,
  getContext,
  getMaster,
  isMuted,
  releaseVoice,
} from "./AudioEngine";

const NOISE_BUFFER_LENGTH = 2048;

let sharedNoiseBuffer: AudioBuffer | null = null;
let sharedNoiseBufferContext: AudioContext | null = null;

/** Lazily builds (once per context) and reuses a short white-noise buffer. */
function getNoiseBuffer(context: AudioContext): AudioBuffer {
  if (sharedNoiseBuffer && sharedNoiseBufferContext === context) {
    return sharedNoiseBuffer;
  }

  const buffer = context.createBuffer(
    1,
    NOISE_BUFFER_LENGTH,
    context.sampleRate,
  );
  const data = buffer.getChannelData(0);
  for (let i = 0; i < NOISE_BUFFER_LENGTH; i += 1) {
    data[i] = Math.random() * 2 - 1;
  }

  sharedNoiseBuffer = buffer;
  sharedNoiseBufferContext = context;
  return buffer;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampPan(pan: number): number {
  return Math.max(-1, Math.min(1, pan));
}

/**
 * Plays a single balloon pop: a falling-pitch sine "body" plus a filtered
 * noise "transient", panned to `pan`. `size` 0..1 maps 0 = smallest/highest
 * pitched balloon to 1 = biggest/deepest. `pitch` scales every frequency —
 * the streak system rides it upward so fast popping audibly heats up. No-ops
 * instantly if audio is unavailable, muted, or the 12-voice cap is full.
 */
export function playPop(size: number, pan: number, pitch = 1): void {
  const context = getContext();
  const master = getMaster();
  if (!context || !master || isMuted()) {
    return;
  }
  if (!claimVoice()) {
    return;
  }

  try {
    const clampedSize = clamp01(size);
    const clampedPan = clampPan(pan);
    // +/-15% random pitch jitter so no two pops sound identical.
    const jitter = (1 + (Math.random() * 2 - 1) * 0.15) * Math.max(0.5, Math.min(2, pitch));
    const now = context.currentTime;

    const panner = context.createStereoPanner();
    panner.pan.setValueAtTime(clampedPan, now);
    panner.connect(master);

    // --- Body: sine oscillator, pitch falls over ~60ms ---
    const bodyDuration = 0.06;
    const bodyGainDuration = 0.09;
    // Bigger balloons start lower (500Hz) than smaller ones (900Hz).
    const bodyStartFreq = Math.max(40, (900 - clampedSize * 400) * jitter);
    const bodyEndFreq = Math.max(40, 180 * jitter);

    const bodyOsc = context.createOscillator();
    bodyOsc.type = "sine";
    bodyOsc.frequency.setValueAtTime(bodyStartFreq, now);
    bodyOsc.frequency.exponentialRampToValueAtTime(
      bodyEndFreq,
      now + bodyDuration,
    );

    const bodyGain = context.createGain();
    bodyGain.gain.setValueAtTime(1, now);
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, now + bodyGainDuration);

    bodyOsc.connect(bodyGain);
    bodyGain.connect(panner);
    bodyOsc.start(now);
    bodyOsc.stop(now + bodyGainDuration + 0.02);

    // --- Transient: bandpass-filtered noise burst, ~25ms decay ---
    const noiseDuration = 0.025;
    const noiseSource = context.createBufferSource();
    noiseSource.buffer = getNoiseBuffer(context);

    const bandpass = context.createBiquadFilter();
    bandpass.type = "bandpass";
    // Smaller balloons ring brighter (closer to 4kHz), bigger ones duller
    // (closer to 1.5kHz).
    const centerFreq = Math.max(200, (4000 - clampedSize * 2500) * jitter);
    bandpass.frequency.setValueAtTime(centerFreq, now);
    bandpass.Q.setValueAtTime(1.2, now);

    const noiseGain = context.createGain();
    noiseGain.gain.setValueAtTime(0.9, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + noiseDuration);

    noiseSource.connect(bandpass);
    bandpass.connect(noiseGain);
    noiseGain.connect(panner);
    noiseSource.start(now);
    noiseSource.stop(now + noiseDuration + 0.01);

    const releaseDelayMs = Math.max(bodyGainDuration, noiseDuration) * 1000 + 40;
    window.setTimeout(() => {
      releaseVoice();
    }, releaseDelayMs);
  } catch {
    releaseVoice();
  }
}

/**
 * Plays a short sparkly 2-note milestone chime (E6 -> B6), ~350ms, quiet.
 * Intended for use every 100th pop. No-ops if audio is unavailable/muted.
 */
export function playChime(): void {
  const context = getContext();
  const master = getMaster();
  if (!context || !master || isMuted()) {
    return;
  }

  try {
    const now = context.currentTime;
    const notes: Array<{ freq: number; start: number; duration: number }> = [
      { freq: 1318.51, start: 0, duration: 0.16 }, // E6
      { freq: 1975.53, start: 0.12, duration: 0.22 }, // B6
    ];

    for (const note of notes) {
      const osc = context.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(note.freq, now + note.start);

      const gain = context.createGain();
      gain.gain.setValueAtTime(0.0001, now + note.start);
      gain.gain.exponentialRampToValueAtTime(0.12, now + note.start + 0.015);
      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        now + note.start + note.duration,
      );

      osc.connect(gain);
      gain.connect(master);
      osc.start(now + note.start);
      osc.stop(now + note.start + note.duration + 0.02);
    }
  } catch {
    // Chime is decorative only — fail silently.
  }
}

/**
 * Milestone fanfare: a rising major-pentatonic run with a shimmer on top,
 * roughly 1.6s. Deliberately bigger than playChime — this fires when the whole
 * world's Grand Tally crosses a threshold, which happens rarely enough to earn
 * the airtime. No-ops if audio is unavailable/muted.
 */
export function playFanfare(): void {
  const context = getContext();
  const master = getMaster();
  if (!context || !master || isMuted()) {
    return;
  }

  try {
    const now = context.currentTime;
    // C5 D5 E5 G5 A5 C6 — pentatonic, so it lands bright with no sour note.
    const run = [523.25, 587.33, 659.25, 783.99, 880.0, 1046.5];

    run.forEach((freq, i) => {
      const start = now + i * 0.085;
      const duration = 0.34 + i * 0.06;

      const osc = context.createOscillator();
      osc.type = "triangle";
      osc.frequency.setValueAtTime(freq, start);

      const shimmer = context.createOscillator();
      shimmer.type = "sine";
      shimmer.frequency.setValueAtTime(freq * 2, start);
      const shimmerGain = context.createGain();
      shimmerGain.gain.setValueAtTime(0.0001, start);
      shimmerGain.gain.exponentialRampToValueAtTime(0.03, start + 0.02);
      shimmerGain.gain.exponentialRampToValueAtTime(0.0001, start + duration * 0.7);

      const gain = context.createGain();
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.11, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

      const pan = context.createStereoPanner();
      pan.pan.setValueAtTime((i / (run.length - 1)) * 1.2 - 0.6, start);

      osc.connect(gain);
      shimmer.connect(shimmerGain);
      shimmerGain.connect(gain);
      gain.connect(pan);
      pan.connect(master);

      osc.start(start);
      shimmer.start(start);
      osc.stop(start + duration + 0.05);
      shimmer.stop(start + duration + 0.05);
    });

    // A low swell underneath so the run lands rather than just stops.
    const swell = context.createOscillator();
    swell.type = "sine";
    swell.frequency.setValueAtTime(130.81, now);
    const swellGain = context.createGain();
    swellGain.gain.setValueAtTime(0.0001, now);
    swellGain.gain.exponentialRampToValueAtTime(0.09, now + 0.5);
    swellGain.gain.exponentialRampToValueAtTime(0.0001, now + 1.8);
    swell.connect(swellGain);
    swellGain.connect(master);
    swell.start(now);
    swell.stop(now + 1.9);
  } catch {
    // Fanfare is decorative only — fail silently.
  }
}

/**
 * Plays a soft ~200ms filtered-noise whoosh for balloon respawn. Very
 * quiet. No-ops if audio is unavailable/muted.
 */
export function playWhoosh(): void {
  const context = getContext();
  const master = getMaster();
  if (!context || !master || isMuted()) {
    return;
  }

  try {
    const now = context.currentTime;
    const duration = 0.2;

    const noiseSource = context.createBufferSource();
    noiseSource.buffer = getNoiseBuffer(context);
    noiseSource.loop = true;

    const filter = context.createBiquadFilter();
    filter.type = "bandpass";
    filter.Q.setValueAtTime(0.7, now);
    filter.frequency.setValueAtTime(300, now);
    filter.frequency.linearRampToValueAtTime(900, now + duration);

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.06, now + duration * 0.35);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    noiseSource.connect(filter);
    filter.connect(gain);
    gain.connect(master);
    noiseSource.start(now);
    noiseSource.stop(now + duration + 0.02);
  } catch {
    // Whoosh is decorative only — fail silently.
  }
}
